import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import "./Appuntamenti.css";
import { RootState } from "../Redux/store/index";
import { AppointmentUI, Options, Availability } from "../lib/types";
import { APPOINTMENT_SETTINGS, insurance, location } from "../lib/config";
import AppointmentService from "../services/api/AppointmentService";
import BookingService from "../services/api/BookingService";
import {
  mapAppointmentsToUI,
  mapAvailabilitiesToUI,
  formatDateForDisplay,
  formatTimeSlot,
  isAppointmentEditable
} from "../lib/bookingMappers";
import { useErrorHandler } from "../hooks/useServices";
import logger from "../services/Logger";
import { url_getPreparation } from "../lib/url";

// Configurazione locale italiana
addLocale("it", {
  firstDayOfWeek: 1,
  dayNames: ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"],
  dayNamesShort: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
  dayNamesMin: ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"],
  monthNames: [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ],
  monthNamesShort: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
  today: "Oggi",
  clear: "Pulisci"
});

const Appuntamenti: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const toast = useRef<Toast>(null);
  const { handleError } = useErrorHandler(toast);

  const [appointments, setAppointments] = useState<AppointmentUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [preparationDialog, setPreparationDialog] = useState(false);
  const [selectedPreparation, setSelectedPreparation] = useState<string>("");
  const [preparations, setPreparations] = useState<Record<string, string>>({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentUI | null>(null);
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [searchingAvailabilities, setSearchingAvailabilities] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState<Availability | null>(null);
  const [processing, setProcessing] = useState(false);

  // Carica appuntamenti al mount
  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    if (!user.patientInfo?.patientId) {
      logger.warn("Cannot load appointments: patientId missing");
      return;
    }

    try {
      setLoading(true);
      logger.debug("Loading appointments", { patientId: user.patientInfo.patientId });

      const backendAppointments = await AppointmentService.getAppointments(user.patientInfo.patientId);
      const mappedAppointments = mapAppointmentsToUI(backendAppointments);

      setAppointments(mappedAppointments);
      logger.info("Appointments loaded successfully", { count: mappedAppointments.length });

      // Pre-carica le preparazioni per tutti gli appuntamenti
      await loadPreparations(mappedAppointments);

    } catch (error) {
      logger.error("Failed to load appointments", error);
      handleError(error, "Appuntamenti.loadAppointments");
    } finally {
      setLoading(false);
    }
  };

  // Pre-carica le preparazioni per tutti gli appuntamenti
  const loadPreparations = async (appointmentsList: AppointmentUI[]) => {
    const token = user.token;
    const prepsMap: Record<string, string> = {};

    // Filtra solo gli appuntamenti con examId e activityNumericId
    const appointmentsWithIds = appointmentsList.filter(
      app => app.examId && app.activityNumericId
    );

    // Carica le preparazioni in parallelo
    const prepPromises = appointmentsWithIds.map(async (app) => {
      try {
        const response = await fetch(
          `${url_getPreparation}?activity_id=${app.activityNumericId}&exam_id=${app.examId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.Result === "OK" && data.Return && data.Return.length > 0 && data.Return[0]) {
            prepsMap[app.id] = data.Return[0];
          }
        }
      } catch (error) {
        logger.debug("Failed to load preparation for appointment", { appointmentId: app.id });
      }
    });

    await Promise.all(prepPromises);
    setPreparations(prepsMap);
  };

  // Mostra dialog preparazione (usa preparazione pre-caricata)
  const showPreparationDialog = (appointmentId: string) => {
    const preparation = preparations[appointmentId];
    if (preparation) {
      setSelectedPreparation(preparation);
      setPreparationDialog(true);
    }
  };

  // Apri dialog elimina
  const openDeleteDialog = (appointment: AppointmentUI) => {
    setSelectedAppointment(appointment);
    setDeleteDialog(true);
  };

  // Conferma eliminazione
  const confirmDelete = async () => {
    if (!selectedAppointment) return;

    try {
      setProcessing(true);
      logger.info("Deleting appointment", { appointmentId: selectedAppointment.id });

      await AppointmentService.deleteAppointment(selectedAppointment.id);

      toast.current?.show({
        severity: "success",
        summary: "Appuntamento eliminato",
        detail: "L'appuntamento è stato cancellato con successo.",
        life: 5000
      });

      setDeleteDialog(false);
      loadAppointments(); // Ricarica lista

    } catch (error) {
      logger.error("Failed to delete appointment", error);
      handleError(error, "Appuntamenti.deleteAppointment");
    } finally {
      setProcessing(false);
    }
  };

  // Apri dialog reschedule
  const openRescheduleDialog = async (appointment: AppointmentUI) => {
    setSelectedAppointment(appointment);
    setRescheduleDialog(true);
    setRescheduleDate(null);
    setAvailabilities([]);
  };

  // Cerca nuove disponibilità per reschedule
  const searchRescheduleAvailabilities = async () => {
    if (!selectedAppointment || !rescheduleDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Attenzione",
        detail: "Seleziona una data per cercare disponibilità."
      });
      return;
    }

    try {
      setSearchingAvailabilities(true);

      const endDate = new Date(rescheduleDate);
      endDate.setMonth(endDate.getMonth() + 1); // Cerca 1 mese avanti

      const backendAvailabilities = await BookingService.searchAvailabilities({
        activityId: selectedAppointment.activityId,
        startDate: rescheduleDate,
        endDate,
        locationId: location,
        insuranceId: insurance,
        resultsNumber: 50
      });

      const mappedAvailabilities = mapAvailabilitiesToUI(backendAvailabilities);
      setAvailabilities(mappedAvailabilities);

      if (mappedAvailabilities.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "Nessuna disponibilità",
          detail: "Non ci sono slot disponibili per la data selezionata."
        });
      }

    } catch (error) {
      logger.error("Failed to search availabilities for reschedule", error);
      handleError(error, "Appuntamenti.searchRescheduleAvailabilities");
    } finally {
      setSearchingAvailabilities(false);
    }
  };

  // Conferma reschedule
  const confirmReschedule = async () => {
    if (!selectedAppointment || !selectedAvailability) return;

    try {
      setProcessing(true);
      logger.info("Rescheduling appointment", {
        appointmentId: selectedAppointment.id,
        newAvailabilityId: selectedAvailability.id
      });

      await AppointmentService.rescheduleAppointment({
        app_lid: selectedAppointment.id,
        new_availability_lid: selectedAvailability.id,
        new_app_clinicdepartmentid: selectedAvailability.resourceId,
        new_app_start_time: selectedAvailability.startTime,
        new_app_end_time: selectedAvailability.endTime,
        new_app_date: selectedAvailability.date
      });

      toast.current?.show({
        severity: "success",
        summary: "Appuntamento modificato",
        detail: "L'appuntamento è stato spostato con successo.",
        life: 5000
      });

      setRescheduleDialog(false);
      setSelectedAvailability(null);
      loadAppointments(); // Ricarica lista

    } catch (error) {
      logger.error("Failed to reschedule appointment", error);
      handleError(error, "Appuntamenti.rescheduleAppointment");
    } finally {
      setProcessing(false);
    }
  };

  // Raggruppa appuntamenti (upcoming/past)
  const { upcoming, past } = appointments.reduce(
    (groups, app) => {
      const appointmentDate = new Date(`${app.date}T${app.startTime}`);
      const now = new Date();

      if (appointmentDate >= now) {
        groups.upcoming.push(app);
      } else {
        groups.past.push(app);
      }

      return groups;
    },
    { upcoming: [] as AppointmentUI[], past: [] as AppointmentUI[] }
  );

  // Render singolo appuntamento
  const renderAppointment = (app: AppointmentUI, isPast: boolean = false) => {
    const editable = isAppointmentEditable(app.date, app.startTime, APPOINTMENT_SETTINGS.MIN_HOURS_THRESHOLD);
    const cardClass = `app-card ${isPast ? 'appointment-past' : 'appointment-upcoming'} ${!editable && !isPast ? 'appointment-not-editable' : ''}`;

    return (
      <Card key={app.id} className={cardClass}>
        <div className="card-title-icon">
          <i className="pi pi-calendar" style={{ fontSize: "1.5rem", color: "var(--primary-color, #229a61)" }}></i>
          <h3 style={{ margin: 0 }}>{app.activityName || "Appuntamento"}</h3>
        </div>

        <div className="singolo-appuntamento-div mt-3">
          <strong>Data:</strong>
          <span>{formatDateForDisplay(app.date)}</span>
        </div>

        <div className="singolo-appuntamento-div">
          <strong>Orario:</strong>
          <span>{formatTimeSlot(app.startTime, app.endTime)}</span>
        </div>

        {app.resourceDescription && (
          <div className="singolo-appuntamento-div">
            <strong>Medico:</strong>
            <span>{app.resourceDescription}</span>
          </div>
        )}

        {app.price && (
          <div className="singolo-appuntamento-div">
            <strong>Prezzo:</strong>
            <span className="availability-price">€{app.price.toFixed(2)}</span>
          </div>
        )}

        {preparations[app.id] && (
          <div className="singolo-appuntamento-div">
            <strong>Preparazione:</strong>
            <i
              className="pi pi-info-circle highlight-icon"
              onClick={() => showPreparationDialog(app.id)}
              title="Visualizza informazioni preparazione"
            ></i>
          </div>
        )}

        {!editable && !isPast && (
          <div className="mt-3 p-2" style={{ backgroundColor: "#fff3cd", borderRadius: "6px", fontSize: "0.9rem" }}>
            ⚠️ Questo appuntamento non può più essere modificato (meno di {APPOINTMENT_SETTINGS.MIN_HOURS_THRESHOLD} ore).
          </div>
        )}

        {!isPast && (
          <div className="singolo-appuntamento-btn-div">
            <Button
              label="Gestisci"
              icon="pi pi-pencil"
              className="appuntamenti-btn"
              onClick={() => openRescheduleDialog(app)}
              disabled={!editable}
            />
            <Button
              label="Elimina"
              icon="pi pi-trash"
              className="appuntamenti-btn elimina-btn"
              onClick={() => openDeleteDialog(app)}
              disabled={!editable}
            />
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="appuntamenti-div">
      <Toast ref={toast} />

      <h2>I Miei Appuntamenti</h2>

      {loading && (
        <div className="flex flex-column justify-content-center align-items-center" style={{ marginTop: "2rem" }}>
          <ProgressSpinner />
          <p style={{ marginTop: "1rem", color: "#666" }}>Ricerca dei tuoi appuntamenti in corso...</p>
        </div>
      )}

      {!loading && appointments.length === 0 && (
        <div className="no-data-message">
          <i className="pi pi-calendar" style={{ fontSize: "3rem", color: "#ccc" }}></i>
          <p>Non hai appuntamenti programmati.</p>
          <p style={{ fontSize: "0.9rem", color: "#888" }}>
            Vai alla sezione "Prenotazioni" per prenotare un nuovo appuntamento.
          </p>
        </div>
      )}

      {!loading && upcoming.length > 0 && (
        <>
          <h3 style={{ marginTop: "2rem" }}>Prossimi Appuntamenti ({upcoming.length})</h3>
          {upcoming.map((app) => renderAppointment(app, false))}
        </>
      )}

      {!loading && past.length > 0 && (
        <>
          <h3 style={{ marginTop: "2rem" }}>Appuntamenti Passati ({past.length})</h3>
          {past.map((app) => renderAppointment(app, true))}
        </>
      )}

      {/* Dialog Preparazione */}
      <Dialog
        header="Informazioni Preparazione Esame"
        visible={preparationDialog}
        className="responsive-dialog"
        onHide={() => setPreparationDialog(false)}
      >
        <p style={{ whiteSpace: "pre-wrap" }}>{selectedPreparation}</p>
      </Dialog>

      {/* Dialog Elimina */}
      <Dialog
        header="Conferma Eliminazione"
        visible={deleteDialog}
        className="responsive-dialog"
        onHide={() => setDeleteDialog(false)}
        footer={
          <>
            <Button label="Annulla" icon="pi pi-times" onClick={() => setDeleteDialog(false)} className="p-button-text" disabled={processing} />
            <Button label={processing ? "Eliminazione..." : "Conferma"} icon={processing ? "pi pi-spin pi-spinner" : "pi pi-check"} className="elimina-btn" onClick={confirmDelete} disabled={processing} />
          </>
        }
      >
        {selectedAppointment && (
          <p>
            Sei sicuro di voler eliminare l'appuntamento del{" "}
            <strong>{formatDateForDisplay(selectedAppointment.date)}</strong> alle{" "}
            <strong>{selectedAppointment.startTime}</strong>?
          </p>
        )}
      </Dialog>

      {/* Dialog Reschedule */}
      <Dialog
        header="Modifica Appuntamento"
        visible={rescheduleDialog}
        className="responsive-dialog"
        style={{ width: "60vw" }}
        onHide={() => setRescheduleDialog(false)}
      >
        {selectedAppointment && (
          <>
            <div className="mb-3">
              <strong>Appuntamento attuale:</strong>
              <p>{formatDateForDisplay(selectedAppointment.date)} alle {selectedAppointment.startTime}</p>
            </div>

            <div className="mb-3">
              <label htmlFor="reschedule-date">Seleziona nuova data:</label>
              <Calendar
                id="reschedule-date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.value || null)}
                minDate={new Date()}
                dateFormat="dd/mm/yy"
                locale="it"
                showIcon
                className="w-full"
              />
            </div>

            <Button
              label={searchingAvailabilities ? "Ricerca..." : "Cerca Disponibilità"}
              icon={searchingAvailabilities ? "pi pi-spin pi-spinner" : "pi pi-search"}
              onClick={searchRescheduleAvailabilities}
              disabled={!rescheduleDate || searchingAvailabilities}
              className="w-full mb-3"
            />

            {searchingAvailabilities && <ProgressSpinner />}

            {availabilities.length > 0 && (
              <div className="mt-3">
                <strong>Disponibilità trovate ({availabilities.length}):</strong>
                <div style={{ maxHeight: "300px", overflowY: "auto" }} className="mt-2">
                  {availabilities.map((avail) => (
                    <div
                      key={avail.id}
                      className={`p-3 mb-2 border-1 border-round cursor-pointer ${selectedAvailability?.id === avail.id ? 'bg-primary-100' : ''}`}
                      style={{ borderColor: selectedAvailability?.id === avail.id ? "var(--primary-color, #229a61)" : "#dee2e6" }}
                      onClick={() => setSelectedAvailability(avail)}
                    >
                      <div><strong>{formatDateForDisplay(avail.date)}</strong></div>
                      <div>{formatTimeSlot(avail.startTime, avail.endTime)}</div>
                      {avail.price && <div className="availability-price">€{avail.price.toFixed(2)}</div>}
                    </div>
                  ))}
                </div>

                <Button
                  label={processing ? "Modifica..." : "Conferma Modifica"}
                  icon={processing ? "pi pi-spin pi-spinner" : "pi pi-check"}
                  onClick={confirmReschedule}
                  disabled={!selectedAvailability || processing}
                  className="w-full mt-3"
                />
              </div>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
};

export default Appuntamenti;
